import React, { useState, useMemo, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { mergeStyleSets, List, DirectionalHint } from '@fluentui/react';


import { runCancelableAction, runShortAction } from 'src/services/utils';
import { CommandsBar } from 'src/components/base/CommandIcon';
import { ListItem } from 'src/components/base/ListItem';
import { TeachingCoachmark } from 'src/components/base/TeachingCoachmark';

import {useModifiedItems, getEditModeButtons, matchesToProvider} from 'src/components/Flows/utils';

import { useIdAsTarget } from 'shared/hooks';
import { FlowstepCategory } from 'shared/utils/flowstep';
import * as Flow from 'shared/utils/flow';


import {officeStore, dataStore, userStore} from 'src/stores';
import { formatActionType } from 'src/components/Scheduler/actionList';
import {AccountType} from "shared/types/flowstep";


const classNames = mergeStyleSets({
  flowsList: {
    width: '100%',
    height: '40vh',
    overflow: 'auto',
  },
});

export const FlowList = (props) => {
  const history = useHistory();

  const {
    allFlows,
    selectedFlow,
    onSelectFlow,
    anyEditModeEnabled,
    onEditModeEnabled,
    onlyOnActiveWorksheet,
    onOnlyOnActiveWorksheet,
    activeWorksheetId,
    license,
  } = props;
  // console.log('FlowList:start: ', props, history);

  //  State
  const [editModeEnabled, setEditModeEnabled] = useState(false);
  const {
    resetItems: resetModifiedItems,
    removeItem: handleRemove,
    moveItem: handleMove,
    items: editModeFlows,
  } = useModifiedItems([], (data) => data.flow.id);
  const [listId, listTarget] = useIdAsTarget('list');

  const filteredFlows = useMemo(() =>
    allFlows.map(flow => ({
      ...flow,
      flowsteps: flow.flowsteps.filter(data => matchesToProvider(data.flowstep, userStore.accountType))
    }))
   ,  [allFlows, activeWorksheetId, onlyOnActiveWorksheet, anyEditModeEnabled]);

  const selectedFlowId = useMemo(() => selectedFlow
    ? filteredFlows.find(x => x.flow.id === selectedFlow.flow.id)?.flow.id
    : null,
    [selectedFlow, filteredFlows]);

  useEffect(() => {
    !selectedFlowId && onSelectFlow(filteredFlows.length ? filteredFlows[0] : null);
  }, [selectedFlowId]);

  const displayedFlows = editModeEnabled ? editModeFlows.modified : filteredFlows;

  // Flows toolbar
  function handleCreateFlow() {
    history.push(Flow.getRoute({}));
  }

  function handleEditModeClick() {
    resetModifiedItems(allFlows);
    handleEditMode(true);
  }

  function handleEditMode(isEnabled) {
    onEditModeEnabled(isEnabled);
    setEditModeEnabled(isEnabled);
  }

  function handleEditModeReset() {
    handleEditMode(false);
  }

  async function handleEditModeApply() {
    const { modified, areMoved, removed } = editModeFlows;
    console.log('FlowList::editModeApply: ', editModeFlows);

    await runShortAction(async () => {
      const areRemoved = !!removed.length;
      areRemoved && await dataStore.deleteFlow(removed.map(x => x.flow));
      areMoved && await dataStore.replaceFlows(modified.map(x => x.flow));
    });

    handleEditMode(false);
  }

  function handleFlowsOnlyOnActiveWorksheet() {
    onOnlyOnActiveWorksheet();
  }

  // Flows toolbar buttons
  const getFlowsToolbar = () => {
    const { flows } = license;

    const buttons = flows.custom.createOrRemove.can
      ? [
        { iconName: 'xappex-new-flowstep', tooltip: 'Create new flow', onClick: handleCreateFlow,
          teaching: { id: 'flows-add-flow'},
        },
        { iconName: 'xappex-edit', tooltip: 'Turn on edit mode', onClick: handleEditModeClick,
          teaching: { id: 'flows-edit-flows' },
        },
      ]
      : [];

    return {
      leftItems: editModeEnabled
        ? [{ iconName: 'Cancel', tooltip: 'Reset all changes', onClick: handleEditModeReset, }]
        : anyEditModeEnabled ? [] : buttons,
      rightItems: editModeEnabled
        ? [{ iconName: 'CheckMark', tooltip: 'Apply changes', onClick: handleEditModeApply }]
        : anyEditModeEnabled
          ? [null] // Placeholder
          : [{
            iconName: onlyOnActiveWorksheet ? 'xappex-current-worksheet' : 'xappex-all-worksheets',
            toggle: true,
            checked: onlyOnActiveWorksheet,
            tooltip: `Switch between displaying flows and flowsteps from all sheets or from current sheet only`,
            onClick: handleFlowsOnlyOnActiveWorksheet,
          }],
    };
  };

  const flowsToolbar = getFlowsToolbar();
  const teaching = { id: 'flows-flows-list', directionalHint: DirectionalHint.bottomCenter };

  // Render
  return (
    <>
      <CommandsBar {...flowsToolbar} />
      <List
        id={listId}
        className={classNames.flowsList}
        items={displayedFlows}
        version={selectedFlowId}
        onRenderCell={(item, index) =>
          <FlowItem
            key={item.flow.id}
            data={item}
            license={license}
            editModeEnabled={editModeEnabled}
            onlyOnActiveWorksheet={onlyOnActiveWorksheet}
            activeWorkhseetId={activeWorksheetId}
            anyEditModeEnabled={anyEditModeEnabled}
            isSelected={!editModeEnabled && item.flow.id === selectedFlowId}
            onSelected={onSelectFlow}
            onRemoved={handleRemove}
            onMoved={handleMove}
            isLast={index === displayedFlows.length - 1}
            isFirst={index === 0}
          />
        }
      />
      { teaching && <TeachingCoachmark target={listTarget} {...teaching} />}
    </>
  )
}


function FlowItem({
  data, isSelected, editModeEnabled, anyEditModeEnabled, activeWorkhseetId, onlyOnActiveWorksheet,
  license, onSelected, onRemoved, onMoved, isLast, isFirst
}) {
  const history = useHistory();
  const { flow } = data;
  const { type, id } = flow;



  async function handleRunFlow() {
    await runCancelableAction(async (cancelToken) => {
      await officeStore.executeFlow(flow, cancelToken);
    });
  }

  function handleEditFlow() {
    history.push(Flow.getRoute({ id: flow.id }))
  }

  function handleSelected() {
    if (!anyEditModeEnabled) {
      onSelected(data);
    }
  }

  function handleRemoved() {
    onRemoved(data);
  }

  function handleMoved(direction) {
    onMoved(data, direction);
  }

  const getButtons = () => {
    const { flowsteps, flows } = license;
    const { pull, push } = flowsteps;

    const editButton = (type === Flow.Type.Push && push.createOrRemove.unlimited.can)
      || (type === Flow.Type.Custom && flows.custom.createOrRemove.can)
      || type === Flow.Type.Pull
      ? { iconName: 'xappex-edit', tooltip: 'Edit flow parameters', onClick: handleEditFlow, }
      : null;

    const baseRunButton = { iconName: 'xappex-run-flowstep', tooltip: 'Run this flow', onClick: handleRunFlow, };
    const runButton = flows.run.can
      ? type === Flow.Type.Pull
        ? pull.runAll.can ? baseRunButton : { licensing: runAll, ...baseRunButton }
        : baseRunButton
      : null;

    const editModeButtons = getEditModeButtons({
      isFirst, isLast, isDeleteDisabled: type !== Flow.Type.Custom,
      onMoveClick: handleMoved, onDeleteClick: handleRemoved
    });

    return editModeEnabled
      ? editModeButtons
      : (anyEditModeEnabled ? [] : [editButton, !Flow.isEmpty(data.flow) && runButton]).filter(Boolean);
  }

  function getSchedulerInfo() {
    const { document_path, document_id } = dataStore.scheduler;
    if (!document_id || !document_path) {
      return 'Login to the scheduler to see available actions';
    }

    const actions = dataStore.actions.filter(x => x.flow_id === id);
    const count = actions.length;
    if (!count) {
      return 'Not scheduled';
    }
    return `${formatActionType(actions[0].schedule)}${count > 1 ? ` and ${count - 1} more time(s)` : ''}`
  }

  function getTitles() {
    const { flowsteps } = data;
    const { pull, push } = flowsteps.reduce((out, { flowstep }) => {

      const { category } = flowstep;
      category === FlowstepCategory.Pull
        ? ++out.pull
        : ++out.push;
      return out;
    }, { pull: 0, push: 0 });

    const scheduler = getSchedulerInfo();
    const total = push + pull;
    const pullText = pull ? `${pull} Pull` : '';
    const pushText = push ? `${push} Push` : '';

    const title = flow.name;
    const subtitle = total
      ? `${[pullText, pushText].filter(Boolean).join(' and ')} ${total > 1 ? 'flow steps' : 'flow step'}`
      : 'No flow steps'
    ;

    if (!onlyOnActiveWorksheet) {
      return { title, subtitle: [subtitle, scheduler] };
    }

    const visible = flowsteps.reduce((out, x) => out + (x.flowstep.worksheetId === activeWorkhseetId), 0);
    return { title, subtitle: [`${subtitle} (${visible} visible)`, scheduler] };
  }

  const titles = useMemo(getTitles, [data, onlyOnActiveWorksheet, activeWorkhseetId]);
  const buttons = getButtons();
  const props = { ...titles, buttons, onClick: handleSelected, isSelected };

  return <ListItem {...props} />;
}


export default FlowList;
